import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import {
  BRAND,
  formatMoney,
  formatTime,
  isCompletedStatus,
  MAX_FULFILLMENT_NOTES,
  STORE_TIME_ZONE,
  type FulfillmentStatus,
  type StaffOrder,
  type StaffOrderAction,
} from "@neus/shared";
import { api } from "../api/client";
import { barcodeLabel, barcodeType, CODE_QUERY, productBarcode, qrCode, twoDimensional } from "../barcodes";
import { CODE_FAILED, CODE_NOT_READY, CodeLoading, PrintButton } from "../components/CodeStatus";
import { BoxesIcon, ClipboardIcon, TruckIcon } from "../components/Icons";
import { PrintLogo } from "../components/layout/Logo";
import { IconCard, PageTitle } from "../components/ui/IconCard";
import { AdminError, AdminTabs, RequireAdmin } from "./Admin";
import { features } from "../features";
import { formatPhone } from "./Contractors";
import styles from "./Admin.module.css";

const message = (err: unknown) => (err instanceof Error ? err.message : "Something went wrong. Please try again.");

const STATUS_LABELS: Record<FulfillmentStatus, string> = {
  open: "To gather",
  ready: "Ready",
  picked_up: "Picked up",
  delivered: "Delivered",
};

function StatusChip({ status }: { status: FulfillmentStatus }) {
  return <span className={`${styles.statusChip} ${styles[`status_${status}`]}`}>{STATUS_LABELS[status]}</span>;
}

/** "Thu, Nov 5" for a YYYY-MM-DD date. */
const day = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
/** "Oct 20, 3:05 pm" in the store's time zone. */
const when = (iso: string) =>
  new Date(iso)
    .toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: STORE_TIME_ZONE,
    })
    .replace(" AM", " am")
    .replace(" PM", " pm");

const collectedCount = (o: StaffOrder) => o.lines.filter((l) => o.fulfillment.collected[l.sku]).length;
const customerName = (o: StaffOrder) =>
  [o.customer.name, o.customer.company].filter(Boolean).join(", ") || o.customer.email || "—";
const orderLink = (o: StaffOrder) => `/admin/orders/${o.kind}/${encodeURIComponent(o.key)}`;

/** When it's due: a job order's needed-by date and how it leaves; an online order's order date. */
function Due({ order }: { order: StaffOrder }) {
  if (order.kind === "store") return <>Ordered {when(order.placedAt)}</>;
  return (
    <>
      {order.delivery ? "Deliver" : "Pickup"} {day(order.neededBy!)}
      {order.delivery && (
        <div className="muted">
          {formatTime(order.delivery.window.from)}–{formatTime(order.delivery.window.to)}
        </div>
      )}
    </>
  );
}

function OrderTable({ orders, completed }: { orders: StaffOrder[]; completed: boolean }) {
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th scope="col">Order</th>
          <th scope="col">Customer</th>
          <th scope="col">{completed ? "Handed over" : "Due"}</th>
          <th scope="col">{completed ? "Total" : "Items"}</th>
          <th scope="col">Status</th>
        </tr>
      </thead>
      <tbody>
        {orders.map((o) => (
          <tr key={`${o.kind}/${o.key}`}>
            <td>
              <Link to={orderLink(o)}>{o.title}</Link>
              <div className="muted">
                {o.kind === "job"
                  ? o.unpaid
                    ? "Job order: unpaid"
                    : "Job order"
                  : o.unpaid
                    ? "Unpaid: pay in store"
                    : "Online"}{" "}
                · {o.number}
              </div>
            </td>
            <td>{customerName(o)}</td>
            <td>
              {completed && o.fulfillment.completed ? (
                <>
                  {when(o.fulfillment.completed.at)}
                  <div className="muted">{o.fulfillment.completed.by}</div>
                </>
              ) : (
                <Due order={o} />
              )}
            </td>
            <td>{completed ? formatMoney(o.total) : `${collectedCount(o)} of ${o.lines.length} collected`}</td>
            <td>
              <StatusChip status={o.status} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function OrderList({ view }: { view: "active" | "completed" }) {
  const completed = view === "completed";
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["staff-orders", view],
    queryFn: () => api.staffOrders(view),
    retry: false,
  });
  return (
    <div className={styles.stack}>
      <PageTitle
        subtitle={
          completed
            ? "Orders that were picked up or delivered, most recent first."
            : features.payInStorePickup
              ? "Online orders, pickup orders to pay in store, and paid job orders to gather, soonest due first."
              : "Online orders and paid job orders to gather, soonest due first."
        }
      >
        {completed ? "Completed orders" : "Orders to fulfill"}
      </PageTitle>
      <AdminTabs />
      <IconCard icon={<BoxesIcon />} title={completed ? "Picked up or delivered" : "Active orders"}>
        {isLoading ? (
          <p className="muted">Loading…</p>
        ) : error ? (
          <AdminError error={error} onRetry={() => void refetch()} />
        ) : !data?.length ? (
          <p className="muted">{completed ? "Nothing handed over yet." : "All caught up: no orders to gather."}</p>
        ) : (
          <OrderTable orders={data} completed={completed} />
        )}
      </IconCard>
    </div>
  );
}

export function AdminOrdersPage() {
  return (
    <div className="container section">
      <div className={styles.page}>
        <RequireAdmin title="Orders to fulfill">
          <OrderList view="active" />
        </RequireAdmin>
      </div>
    </div>
  );
}

export function AdminCompletedOrdersPage() {
  return (
    <div className="container section">
      <div className={styles.page}>
        <RequireAdmin title="Completed orders">
          <OrderList view="completed" />
        </RequireAdmin>
      </div>
    </div>
  );
}

/** A QR code linking back to the order page, and a barcode per item for the checkout scanners. */
async function printCodes(url: string, lines: StaffOrder["lines"]) {
  const [qr, ...barcodes] = await Promise.all([qrCode(url), ...lines.map((l) => productBarcode(l))]);
  return { qr, barcodes: Object.fromEntries(lines.map((l, i) => [l.sku, barcodes[i]])) };
}

/** Why an item has no barcode: the store's type needs a GTIN this product doesn't have (or can't use). */
const noBarcode = `No ${barcodeLabel} barcode: the product has no GTIN for it.`;

/** One item's barcode on screen, at a size a reader can scan (2 inches wide or more, or 1 inch square). */
function ItemBarcode({ line }: { line: StaffOrder["lines"][number] }) {
  const { data, isPending, isError } = useQuery({
    queryKey: ["barcode", barcodeType, line.sku, line.gtin],
    // Null for no barcode: a query can't return undefined.
    queryFn: async () => (await productBarcode(line)) ?? null,
    ...CODE_QUERY,
  });
  if (isPending)
    return (
      <div className={styles.screenBarcode}>
        <CodeLoading shape={twoDimensional ? "square" : "barcode"} />
      </div>
    );
  // No alert or button of its own: one failure would repeat them on every line.
  if (isError)
    return (
      <p className={`muted ${styles.screenBarcode}`}>Couldn&rsquo;t load the barcode. Reload the page to try again.</p>
    );
  if (!data) return <p className={`muted ${styles.screenBarcode}`}>{noBarcode}</p>;
  return (
    <div
      className={`${styles.screenBarcode} ${twoDimensional ? styles.square : ""}`}
      dangerouslySetInnerHTML={{ __html: data }}
    />
  );
}

const UNPAID_TEXT = "Unpaid order – ring up as new purchase";

/**
 * A pay-in-store pickup order's warning, on its page and its pick sheet: a big dollar sign crossed out, so nobody
 * hands it over as if it were paid for.
 */
function UnpaidMark() {
  return (
    <div className={styles.unpaid}>
      <svg viewBox="0 0 100 100" aria-hidden="true" className={styles.unpaidIcon}>
        <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="7" />
        <text x="50" y="74" textAnchor="middle" fontSize="70" fontWeight="700" fill="currentColor">
          $
        </text>
        <path d="M20 20 80 80M80 20 20 80" stroke="currentColor" strokeWidth="9" strokeLinecap="round" />
      </svg>
      <strong className={styles.unpaidText}>{UNPAID_TEXT}</strong>
    </div>
  );
}

/** The customer's contractor pricing on the order, so staff (and the register) charge it. */
function ContractorPricingNote({ order, className }: { order: StaffOrder; className?: string }) {
  const p = order.contractorPricing;
  if (!p) return null;
  const parts = [
    p.specialPrices ? `${p.specialPrices} item${p.specialPrices === 1 ? "" : "s"} at a special price` : "",
    p.discount
      ? `${p.discountPercent ? `${p.discountPercent}% ` : ""}job order discount (−${formatMoney(p.discount)})`
      : "",
  ].filter(Boolean);
  return (
    <p className={className ?? styles.contractorPricing}>
      <strong>Contractor pricing:</strong> {parts.join(" · ")}
    </p>
  );
}

/** A line's contractor price: the special price with the regular one struck through, or its job order discount. */
function LinePrice({ line }: { line: StaffOrder["lines"][number] }) {
  if (line.regularUnitPrice !== undefined) {
    return (
      <span className={styles.linePrice}>
        Contractor price {formatMoney(line.unitPrice)} each <s>{formatMoney(line.regularUnitPrice)}</s>
      </span>
    );
  }
  if (line.discount) {
    return (
      <span className={styles.linePrice}>
        {formatMoney(line.unitPrice)} each, job order discount −{formatMoney(line.discount)}
      </span>
    );
  }
  return null;
}

/** Job orders and pay-in-store pickup orders print a pick sheet. */
const printable = (o: StaffOrder) => o.kind === "job" || Boolean(o.unpaid);

/** Only the title and QR code print: every job order sheet with anonymous pick sheets on, or the contractor chose it. */
const anonymousSheet = (o: StaffOrder) => o.kind === "job" && (features.anonymousPickSheets || Boolean(o.anonymous));

/**
 * An order's codes. A job order's QR code holds the short link /api/qr/{number}, which redirects to the order's page;
 * a pickup order's, the staff page itself.
 */
function useOrderCodes(order: StaffOrder) {
  const url =
    order.kind === "job"
      ? `${window.location.origin}/api/qr/${encodeURIComponent(order.number)}`
      : `${window.location.origin}${orderLink(order)}`;
  // An anonymous pick sheet has no item list, so no barcodes.
  const lines = anonymousSheet(order) ? [] : order.lines;
  return useQuery({
    queryKey: ["print-codes", url, barcodeType, lines],
    queryFn: () => printCodes(url, lines),
    ...CODE_QUERY,
  });
}

function OrderPrintButton({ order }: { order: StaffOrder }) {
  const { isSuccess, isError } = useOrderCodes(order);
  return <PrintButton ready={isSuccess} failed={isError} />;
}

/** The order's QR code on screen, to open the order on another device. */
function OrderQr({ order }: { order: StaffOrder }) {
  const { data: codes, isPending } = useOrderCodes(order);
  // If it can't load, the Print button beside it says so.
  if (!codes && !isPending) return null;
  return (
    <figure className={styles.screenQr}>
      {codes ? <div dangerouslySetInnerHTML={{ __html: codes.qr }} /> : <CodeLoading shape="qr" />}
      <figcaption className="muted">Scan to open the order</figcaption>
    </figure>
  );
}

/**
 * The pick sheet a job order or pickup order prints (the "Print" button, or the browser's own print): only this
 * sheet prints, not the page. It sits outside #root, which print hides. An anonymous sheet shows only the job's
 * title and QR code, so a sheet left lying around the store doesn't say who the order is for; staff gather the
 * items from the order's page.
 */
function PrintSheet({ order }: { order: StaffOrder }) {
  const { data: codes, isError } = useOrderCodes(order);
  const missing = isError ? CODE_FAILED : CODE_NOT_READY;
  return createPortal(
    <div className={styles.printSheet}>
      <header className={styles.printHead}>
        <PrintLogo className={styles.printLogo} />
        <div>
          <p className="muted">
            {BRAND.name} · {order.kind === "job" ? "Job order" : "Pickup order"}
          </p>
          <h1>{order.title}</h1>
          {!anonymousSheet(order) && (
            <>
              <p>
                {order.number} · {customerName(order)}
              </p>
              <p>
                <Due order={order} />
              </p>
              {order.delivery && (
                <p>
                  {order.delivery.address.line1}
                  {order.delivery.address.line2 && <>, {order.delivery.address.line2}</>}, {order.delivery.address.city}
                  , {order.delivery.address.state} {order.delivery.address.postalCode} · {order.delivery.contactName},{" "}
                  {formatPhone(order.delivery.contactPhone)}
                </p>
              )}
            </>
          )}
        </div>
        <figure className={styles.printQr}>
          {codes ? <div dangerouslySetInnerHTML={{ __html: codes.qr }} /> : <p>{missing}</p>}
          <figcaption>Scan to open the order</figcaption>
        </figure>
      </header>
      {order.unpaid && <UnpaidMark />}
      {!anonymousSheet(order) && (
        <>
          <p className={styles.printReminder}>
            Ticking this sheet doesn&rsquo;t update anything. Once the items are gathered, tick them on the
            order&rsquo;s page too (scan the QR code): that{" "}
            {order.kind === "job" ? "takes them off the stock count and marks" : "marks"} the order ready.
          </p>
          <ContractorPricingNote order={order} className={styles.printPricing} />
          {/* Each barcode on the other side from the one above it, so a scanner can't catch two at once. */}
          <table className={styles.printTable}>
            <thead>
              <tr>
                <th scope="col">Ready</th>
                <th scope="col">Item</th>
              </tr>
            </thead>
            <tbody>
              {order.lines.map((l) => (
                <tr key={l.sku}>
                  <td className={styles.printBox}>
                    <span>{order.fulfillment.collected[l.sku] ? "✓" : ""}</span>
                  </td>
                  <td>
                    <div className={styles.printItem}>
                      <span>
                        {l.qty} × {l.name}
                        {features.storeLocations && l.location && (
                          <strong className={styles.printLocation}>{l.location}</strong>
                        )}
                        <LinePrice line={l} />
                      </span>
                      {codes?.barcodes[l.sku] ? (
                        <span
                          className={`${styles.printBarcode} ${twoDimensional ? styles.square : ""}`}
                          dangerouslySetInnerHTML={{ __html: codes.barcodes[l.sku]! }}
                        />
                      ) : (
                        <span className={styles.printBarcode}>
                          {l.sku}
                          <br />
                          {codes ? noBarcode : missing}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {order.description && <p>Customer notes: {order.description}</p>}
          {order.fulfillment.notes && <p>Staff notes: {order.fulfillment.notes}</p>}
        </>
      )}
    </div>,
    document.body,
  );
}

function OrderDetail({ kind, orderKey }: { kind: string; orderKey: string }) {
  const qc = useQueryClient();
  const queryKey = ["staff-order", kind, orderKey];
  const {
    data: order,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => api.staffOrder(kind, orderKey),
    retry: false,
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const [notes, setNotes] = useState<string | null>(null);
  const [barcodes, setBarcodes] = useState<Record<string, boolean>>({});

  if (isLoading) return <p className="muted">Loading…</p>;
  if (error || !order) return <AdminError error={error} onRetry={() => void refetch()} />;

  const done = isCompletedStatus(order.status);
  const draft = notes ?? order.fulfillment.notes;
  const send = async (action: StaffOrderAction, label: string, ok?: string) => {
    setBusy(label);
    setStatus(null);
    try {
      const updated = await api.updateStaffOrder(kind, orderKey, action);
      qc.setQueryData(queryKey, updated);
      void qc.invalidateQueries({ queryKey: ["staff-orders"] });
      if (action.action === "notes") setNotes(null);
      if (ok) setStatus({ ok: true, text: ok });
    } catch (err) {
      setStatus({ ok: false, text: message(err) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={styles.stack}>
      <div className={styles.titleRow}>
        <PageTitle
          subtitle={`${order.kind === "job" ? "Job order" : "Online order"} ${order.number} · ${customerName(order)}`}
        >
          {order.title}
        </PageTitle>
        {order.kind === "job" && <OrderQr order={order} />}
      </div>
      {order.unpaid && <UnpaidMark />}
      <div className={styles.row}>
        <StatusChip status={order.status} />
        <span className="muted">
          {collectedCount(order)} of {order.lines.length} items collected · {formatMoney(order.total)}
          {order.kind === "job" && anonymousSheet(order) && " · Anonymous pick sheet"}
        </span>
        <Link to={done ? "/admin/orders/completed" : "/admin/orders"}>{done ? "Completed orders" : "All orders"}</Link>
        {printable(order) && <OrderPrintButton order={order} />}
      </div>
      <ContractorPricingNote order={order} />
      {printable(order) && <PrintSheet order={order} />}
      {status && <div className={status.ok ? "notice" : "notice notice--warn"}>{status.text}</div>}

      <IconCard icon={<BoxesIcon />} title="Items">
        <p className="muted">
          Tick each item as it&rsquo;s gathered.
          {order.kind === "job"
            ? " Collecting a job order item takes it off the stock count."
            : order.unpaid
              ? " Not paid yet: ringing it up at the register lowers the stock."
              : " This order lowered stock when it was paid."}
        </p>
        <ul className={styles.checklist}>
          {order.lines.map((l) => {
            const got = order.fulfillment.collected[l.sku];
            return (
              <li key={l.sku}>
                <div className={styles.checkLine}>
                  <label className={styles.checkItem}>
                    <input
                      type="checkbox"
                      checked={Boolean(got)}
                      disabled={done || busy !== null}
                      onChange={(e) => void send({ action: "collect", sku: l.sku, collected: e.target.checked }, l.sku)}
                    />
                    <strong>
                      {l.qty} × {l.name}
                    </strong>
                  </label>
                  <LinePrice line={l} />
                  {features.storeLocations && l.location && <span className={styles.location}>{l.location}</span>}
                  {/* Outside the label, so showing the barcode doesn't tick the item. */}
                  <button
                    type="button"
                    className={styles.skuButton}
                    aria-expanded={Boolean(barcodes[l.sku])}
                    title={barcodes[l.sku] ? "Hide the barcode" : "Show the barcode"}
                    onClick={() => setBarcodes((b) => ({ ...b, [l.sku]: !b[l.sku] }))}
                  >
                    {l.sku}
                  </button>
                  {got && (
                    <span className="muted">
                      collected {when(got.at)} by {got.by}
                    </span>
                  )}
                </div>
                {barcodes[l.sku] && <ItemBarcode line={l} />}
              </li>
            );
          })}
        </ul>
      </IconCard>

      <IconCard icon={<TruckIcon />} title={order.delivery ? "Delivery to the job site" : "Handover"}>
        {order.delivery ? (
          <dl className={styles.details}>
            <dt>When</dt>
            <dd>
              {day(order.neededBy!)}, {formatTime(order.delivery.window.from)}–{formatTime(order.delivery.window.to)}
            </dd>
            <dt>Address</dt>
            <dd>
              {order.delivery.address.line1}
              {order.delivery.address.line2 && <>, {order.delivery.address.line2}</>}
              <br />
              {order.delivery.address.city}, {order.delivery.address.state} {order.delivery.address.postalCode}
            </dd>
            <dt>Supervisor</dt>
            <dd>
              {order.delivery.contactName},{" "}
              <a href={`tel:${order.delivery.contactPhone}`}>{formatPhone(order.delivery.contactPhone)}</a>
            </dd>
          </dl>
        ) : (
          <>
            {order.shipTo && (
              <dl className={styles.details}>
                <dt>Ship to</dt>
                <dd>
                  {order.shipTo.name}
                  <br />
                  {order.shipTo.line1}
                  {order.shipTo.line2 && <>, {order.shipTo.line2}</>}
                  <br />
                  {order.shipTo.city}, {order.shipTo.state} {order.shipTo.postalCode}
                </dd>
                <dt>Shipping paid</dt>
                <dd>{order.shipping ? formatMoney(order.shipping) : "Free"}</dd>
              </dl>
            )}
            <p>
              {order.kind === "job"
                ? `Pickup at the store, needed by ${day(order.neededBy!)}.`
                : order.unpaid
                  ? "Pickup at the store, not paid. Ring it up at the register as a new purchase, then hand it over."
                  : order.shipTo
                    ? ""
                    : "Online order (placed before shipping addresses were kept; see the order in Stripe)."}
              {order.customer.email && (
                <>
                  {" "}
                  Customer: <a href={`mailto:${order.customer.email}`}>{order.customer.email}</a>
                </>
              )}
            </p>
          </>
        )}
        {order.description && <p className="muted">Customer notes: {order.description}</p>}
        {order.kind === "job" && order.unpaid && (
          <p>
            Placed without a deposit. Take the full {formatMoney(order.total)} at the register at pickup or delivery.
          </p>
        )}
        {order.balance && (
          <p className={order.balance.invoiced ? "muted" : undefined}>
            {order.balance.invoiced
              ? `Balance of ${formatMoney(order.balance.amount)} invoiced.`
              : `Deposit paid. The balance of ${formatMoney(order.balance.amount)} is billed to the card on file when the order is ready; it hasn't been invoiced yet.`}
          </p>
        )}
        {done && order.fulfillment.completed ? (
          <div className="notice">
            {STATUS_LABELS[order.status]} {when(order.fulfillment.completed.at)} · {order.fulfillment.completed.by}
          </div>
        ) : (
          <div className={styles.row}>
            <button
              type="button"
              className="btn"
              disabled={order.status !== "ready" || busy !== null}
              onClick={() => void send({ action: "complete", outcome: "picked_up" }, "picked_up", "Marked picked up.")}
            >
              Mark picked up
            </button>
            {(order.kind === "job" || !order.unpaid) && (
              <button
                type="button"
                className="btn"
                disabled={order.status !== "ready" || busy !== null}
                onClick={() =>
                  void send({ action: "complete", outcome: "delivered" }, "delivered", "Marked delivered.")
                }
              >
                Mark delivered
              </button>
            )}
            {order.status !== "ready" && <span className="muted">Collect every item first.</span>}
          </div>
        )}
      </IconCard>

      <IconCard icon={<ClipboardIcon />} title="Staff notes">
        <label className={styles.field}>
          <span>Only staff see these notes</span>
          <textarea
            className="input"
            rows={4}
            maxLength={MAX_FULFILLMENT_NOTES}
            value={draft}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Bin location, substitutions, who picked it up…"
          />
        </label>
        <div className={styles.row}>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={busy !== null || draft === order.fulfillment.notes}
            onClick={() => void send({ action: "notes", notes: draft }, "notes", "Notes saved.")}
          >
            {busy === "notes" ? "Saving…" : "Save notes"}
          </button>
          {order.fulfillment.updatedAt && (
            <span className="muted">
              Last change {when(order.fulfillment.updatedAt)} by {order.fulfillment.updatedBy}
            </span>
          )}
        </div>
      </IconCard>
    </div>
  );
}

export function AdminOrderPage() {
  const { kind = "", key = "" } = useParams();
  return (
    <div className="container section">
      <div className={styles.page}>
        <RequireAdmin title="Order">
          <OrderDetail key={`${kind}/${key}`} kind={kind} orderKey={key} />
        </RequireAdmin>
      </div>
    </div>
  );
}
